import {
  createContext,
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { AutoCompleteShipInfo, TargetInfoType } from '@ais-ui/websockets';
import { ShipSearchResult } from './Search/useShipSearch';
import { Geometry } from 'ol/geom';

interface AISMapContextProps {
  children: ReactNode;
}

export type Port = {
  id: string;
  name: string;
  country: string;
  locode: string;
  geometry: Geometry;
};

interface AISMapContextValue {
  trigger: number;
  setTrigger: Dispatch<SetStateAction<number>>;
  isClickingShip: boolean;
  setIsClickingShip: Dispatch<SetStateAction<boolean>>;
  createdAt: Date | undefined;
  setCreatedAt: Dispatch<SetStateAction<Date | undefined>>;
  targetCountInfo: string | null;
  setTargetCountInfo: Dispatch<SetStateAction<string | null>>;
  overlay: string | null;
  setOverlay: Dispatch<SetStateAction<string | null>>;
  zoomToShip: AutoCompleteShipInfo | null;
  setZoomToShip: (
    newZoomToShip: AutoCompleteShipInfo | ShipSearchResult | null,
  ) => void;
  showChooseTargetPanel: boolean;
  setShowChooseTargetPanel: Dispatch<SetStateAction<boolean>>;
  onChooseTarget: MutableRefObject<((target: TargetInfoType) => void) | null>;
  onRejectTarget: MutableRefObject<(() => void) | null>;
  chooseTargetTypes: string[];
  setChooseTargetTypes: Dispatch<SetStateAction<string[]>>;
  cursorPosition: {
    lat: string;
    lon: string;
  } | null;
  setCursorPosition: Dispatch<
    SetStateAction<{
      lat: string;
      lon: string;
    } | null>
  >;
}

const AISMapContext = createContext<AISMapContextValue | undefined>(undefined);

export const useChooseTarget = () => {
  const [showChooseTargetPanel, setShowChooseTargetPanel] =
    useState<boolean>(false);
  const onChooseTarget = useRef<((target: TargetInfoType) => void) | null>(
    null,
  );
  const onRejectTarget = useRef<(() => void) | null>(null);

  const [chooseTargetTypes, setChooseTargetTypes] = useState<string[]>([]);

  return {
    showChooseTargetPanel,
    setShowChooseTargetPanel,
    onChooseTarget,
    onRejectTarget,
    chooseTargetTypes,
    setChooseTargetTypes,
  };
};

export const AISMapProvider = ({ children }: AISMapContextProps) => {
  const [trigger, setTrigger] = useState<number>(0);

  const [isClickingShip, setIsClickingShip] = useState<boolean>(false);

  const {
    showChooseTargetPanel,
    setShowChooseTargetPanel,
    onChooseTarget,
    onRejectTarget,
    chooseTargetTypes,
    setChooseTargetTypes,
  } = useChooseTarget();

  const [zoomToShip, _setZoomToShip] = useState<AutoCompleteShipInfo | null>(
    null,
  );
  const [createdAt, setCreatedAt] = useState<Date | undefined>();
  const [targetCountInfo, setTargetCountInfo] = useState<string | null>(null);
  const [overlay, setOverlay] = useState<string | null>(null);
  const [cursorPosition, setCursorPosition] = useState<{
    lat: string;
    lon: string;
  } | null>(null);

  const setZoomToShip = useCallback(
    (newZoomToShip: AutoCompleteShipInfo | ShipSearchResult | null) => {
      if (!newZoomToShip) {
        _setZoomToShip(null);
        return;
      }

      const newZoomToShipAutoComplete = {
        callSign: (newZoomToShip as any)?.callSign
          ? (newZoomToShip as any).callSign
          : null,
        imo: newZoomToShip?.imo !== null && newZoomToShip?.imo !== undefined ? newZoomToShip.imo.toString() : null,
        mmsi: newZoomToShip?.mmsi !== null && newZoomToShip?.mmsi !== undefined ? newZoomToShip.mmsi.toString() : null,
        name: newZoomToShip?.name ? newZoomToShip.name : null,
        type: (newZoomToShip as any)?.type ? (newZoomToShip as any).type : null,
        filteredOut: (newZoomToShip as any)?.filteredOut,
        forceView: (newZoomToShip as any)?.forceView,
      };
      _setZoomToShip(newZoomToShipAutoComplete);
    },
    [],
  );

  const value = {
    trigger,
    setTrigger,
    isClickingShip,
    setIsClickingShip,
    createdAt,
    setCreatedAt,
    overlay,
    setOverlay,
    zoomToShip,
    setZoomToShip,
    showChooseTargetPanel,
    setShowChooseTargetPanel,
    onChooseTarget,
    onRejectTarget,
    chooseTargetTypes,
    setChooseTargetTypes,
    cursorPosition,
    setCursorPosition,
    targetCountInfo,
    setTargetCountInfo,
  };

  return (
    <AISMapContext.Provider value={value}>{children}</AISMapContext.Provider>
  );
};

export const useAISMapContext = () => {
  const context = useContext(AISMapContext);
  if (!context) {
    throw new Error(
      'useAISMapContext must be used within a AISMapContextProvider',
    );
  }
  return context;
};
